import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, ArrowUpDown, GripVertical, Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { PlateEditor } from '~/components/editor';
import type { PlateEditorRef } from '~/components/editor/plate-editor';
import { useMedicamentsTableFilteringStore } from '~/stores/medicaments-table-filtering-store';
import { useTreatmentRegionsTableFilteringStore } from '~/stores/treatment-regions-table-filtering-store';
import Content from '../components/layout/Content';
import { toast } from 'sonner';

// ============================================
// Types
// ============================================

type MedicamentUnit = {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
};

type Medicament = {
    id: string;
    name: string;
    quickAccess: boolean;
    unit: { id: string; name: string } | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
};

type TreatmentRegion = {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
};

// ============================================
// Column Definitions
// ============================================

const SortableHeader = ({ column, label }: { column: any; label: string }) => (
    <Button
        variant='ghost'
        onClick={() => {
            const currentSort = column.getIsSorted();
            if (currentSort === 'asc') {
                column.toggleSorting(true);
            } else if (currentSort === 'desc') {
                column.clearSorting();
            } else {
                column.toggleSorting(false);
            }
        }}
    >
        {label}
        {column.getIsSorted() === 'asc' ? (
            <ArrowUp className='ml-2 h-4 w-4' />
        ) : column.getIsSorted() === 'desc' ? (
            <ArrowDown className='ml-2 h-4 w-4' />
        ) : (
            <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
        )}
    </Button>
);

const medicamentColumns: ColumnDef<Medicament>[] = [
    {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column} label='Name' />,
        enableSorting: true,
    },
    {
        id: 'unit',
        header: 'Einheit',
        cell: ({ row }) => row.original.unit?.name ?? '–',
    },
];

const unitColumns: ColumnDef<MedicamentUnit>[] = [
    {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column} label='Name' />,
        enableSorting: true,
    },
];

const regionColumns: ColumnDef<TreatmentRegion>[] = [
    {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column} label='Name' />,
        enableSorting: true,
    },
];

// ============================================
// Medicament Sheet
// ============================================

const SortableRegionItem = ({
    region,
    position,
    onRemove,
}: {
    region: { id: string; name: string };
    position: number;
    onRemove: () => void;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: region.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <li ref={setNodeRef} style={style} className='flex items-center gap-2 rounded-md border bg-white px-2 py-1.5'>
            <button
                type='button'
                className='cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground'
                {...attributes}
                {...listeners}
            >
                <GripVertical className='h-4 w-4' />
            </button>
            <span className='w-5 text-xs text-muted-foreground tabular-nums'>{position}.</span>
            <Checkbox id={`region-${region.id}`} checked onCheckedChange={onRemove} />
            <label htmlFor={`region-${region.id}`} className='text-sm cursor-pointer flex-1'>
                {region.name}
            </label>
        </li>
    );
};

interface MedicamentSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    medicament: Medicament | null;
    onSave: (data: { name: string; unitId?: string; regionIds: string[]; quickAccess: boolean }) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const MedicamentSheet = ({ open, onOpenChange, medicament, onSave, onDelete, isSaving }: MedicamentSheetProps) => {
    const [name, setName] = useState('');
    const [unitId, setUnitId] = useState('');
    const [quickAccess, setQuickAccess] = useState(false);
    const [selectedRegionIds, setSelectedRegionIds] = useState<string[]>([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const { data: allUnits = [] } = trpc.medicationConfig.getAllUnits.useQuery();
    const { data: allRegions = [] } = trpc.medicationConfig.getAllTreatmentRegions.useQuery();
    const { data: medicamentRegionIds } = trpc.medicationConfig.getMedicamentRegions.useQuery(
        { medicamentId: medicament?.id ?? '' },
        { enabled: !!medicament?.id }
    );

    useEffect(() => {
        if (medicament) {
            setName(medicament.name);
            setUnitId(medicament.unit?.id ?? '');
            setQuickAccess(medicament.quickAccess);
        } else {
            setName('');
            setUnitId('');
            setQuickAccess(false);
            setSelectedRegionIds([]);
        }
    }, [medicament, open]);

    useEffect(() => {
        if (medicamentRegionIds) {
            setSelectedRegionIds(medicamentRegionIds);
        }
    }, [medicamentRegionIds]);

    const toggleRegion = (regionId: string) => {
        setSelectedRegionIds((prev) =>
            prev.includes(regionId) ? prev.filter((id) => id !== regionId) : [...prev, regionId]
        );
    };

    const regionSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const selectedRegions = useMemo(() => {
        const regionMap = new Map(allRegions.map((r) => [r.id, r]));
        return selectedRegionIds
            .map((id) => regionMap.get(id))
            .filter((r): r is { id: string; name: string } => !!r);
    }, [allRegions, selectedRegionIds]);

    const unselectedRegions = useMemo(
        () => allRegions.filter((r) => !selectedRegionIds.includes(r.id)),
        [allRegions, selectedRegionIds]
    );

    const handleRegionDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setSelectedRegionIds((prev) => {
            const oldIndex = prev.indexOf(active.id as string);
            const newIndex = prev.indexOf(over.id as string);
            if (oldIndex === -1 || newIndex === -1) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim()) {
            onSave({ name: name.trim(), unitId: unitId || undefined, regionIds: selectedRegionIds, quickAccess });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {medicament ? 'Medikament bearbeiten' : 'Neues Medikament'}
                            </SheetTitle>
                            <SheetDescription>
                                {medicament
                                    ? 'Bearbeiten Sie die Daten des Medikaments.'
                                    : 'Geben Sie die Daten für das neue Medikament ein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='med-name'>Name</Label>
                                <Input
                                    id='med-name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='z.B. Hyaluronsäure'
                                    autoFocus
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label>Einheit</Label>
                                <Select value={unitId} onValueChange={setUnitId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder='Einheit wählen...' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {allUnits.map((u) => (
                                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className='text-xs text-muted-foreground'>
                                    Die Maßeinheit, in der das Medikament dosiert wird.
                                </p>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label>Behandlungszonen</Label>
                                <div className='border rounded-md p-3 space-y-2 max-h-144 overflow-y-auto'>
                                    {allRegions.length === 0 && (
                                        <p className='text-sm text-muted-foreground'>Keine Behandlungszonen vorhanden.</p>
                                    )}
                                    {selectedRegions.length > 0 && (
                                        <DndContext
                                            sensors={regionSensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleRegionDragEnd}
                                        >
                                            <SortableContext
                                                items={selectedRegions.map((r) => r.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <ul className='flex flex-col gap-1.5'>
                                                    {selectedRegions.map((region, idx) => (
                                                        <SortableRegionItem
                                                            key={region.id}
                                                            region={region}
                                                            position={idx + 1}
                                                            onRemove={() => toggleRegion(region.id)}
                                                        />
                                                    ))}
                                                </ul>
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                    {selectedRegions.length > 0 && unselectedRegions.length > 0 && <Separator />}
                                    {unselectedRegions.map((region) => (
                                        <div key={region.id} className='flex items-center gap-2'>
                                            <Checkbox
                                                id={`region-${region.id}`}
                                                checked={false}
                                                onCheckedChange={() => toggleRegion(region.id)}
                                            />
                                            <label
                                                htmlFor={`region-${region.id}`}
                                                className='text-sm cursor-pointer'
                                            >
                                                {region.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <p className='text-xs text-muted-foreground'>
                                    Wählen Sie die Behandlungszonen, für die dieses Medikament verwendet werden kann.
                                    Die Reihenfolge der ausgewählten Zonen (per Drag & Drop) bestimmt die Anzeige in
                                    der Dokumentation.
                                </p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <Checkbox
                                    id='quick-access'
                                    checked={quickAccess}
                                    onCheckedChange={(checked) => setQuickAccess(checked === true)}
                                />
                                <label htmlFor='quick-access' className='text-sm cursor-pointer'>
                                    Schnellzugriff
                                </label>
                            </div>
                        </div>
                        <SheetFooter>
                            {medicament && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim()}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {medicament ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Medikament löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie das Medikament &quot;{medicament?.name}&quot; wirklich löschen? Diese Aktion
                            kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ============================================
// Treatment Region Sheet
// ============================================

interface TreatmentRegionSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    region: TreatmentRegion | null;
    onSave: (data: { name: string }) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const TreatmentRegionSheet = ({ open, onOpenChange, region, onSave, onDelete, isSaving }: TreatmentRegionSheetProps) => {
    const [name, setName] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    useEffect(() => {
        if (region) {
            setName(region.name);
        } else {
            setName('');
        }
    }, [region, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim()) {
            onSave({ name: name.trim() });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {region ? 'Behandlungszone bearbeiten' : 'Neue Behandlungszone'}
                            </SheetTitle>
                            <SheetDescription>
                                {region
                                    ? 'Bearbeiten Sie den Namen der Behandlungszone.'
                                    : 'Geben Sie den Namen für die neue Behandlungszone ein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='region-name'>Name</Label>
                                <Input
                                    id='region-name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='z.B. Nasolabialfalte'
                                    autoFocus
                                />
                            </div>
                        </div>
                        <SheetFooter>
                            {region && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim()}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {region ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Behandlungszone löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie die Behandlungszone &quot;{region?.name}&quot; wirklich löschen? Diese Aktion
                            kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ============================================
// Generic Data Table Helpers
// ============================================

const TableSkeleton = ({ columnCount }: { columnCount: number }) => (
    <div className='bg-white rounded-lg shadow-sm p-6'>
        <div className='flex items-center justify-between py-4'>
            <Skeleton className='h-10 w-[250px]' />
            <Skeleton className='h-10 w-[150px]' />
        </div>
        <div className='rounded-md border border-gray-200'>
            <Table>
                <TableHeader>
                    <TableRow>
                        {Array.from({ length: columnCount }).map((_, index) => (
                            <TableHead key={index}>
                                <Skeleton className='h-6 w-full' />
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Array.from({ length: 5 }).map((_, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {Array.from({ length: columnCount }).map((_, colIndex) => (
                                <TableCell key={colIndex}>
                                    <Skeleton className='h-6 w-full' />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    </div>
);

// ============================================
// Medicaments Data Table
// ============================================

const MedicamentsDataTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);

    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useMedicamentsTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedMedicament, setSelectedMedicament] = useState<Medicament | null>(null);

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        const urlSortField = searchParams.get('med_sort');
        const urlSortDir = searchParams.get('med_dir') || 'asc';

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
            }
        } else if (storeSorting.length > 0) {
            newParams.set('med_sort', storeSorting[0].id);
            newParams.set('med_dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        }

        const urlPage = searchParams.get('med_page');
        const urlSize = searchParams.get('med_size');

        if (urlPage !== null || urlSize !== null) {
            const pageIndex = urlPage !== null ? Math.max(0, parseInt(urlPage, 10) || 0) : storePagination.pageIndex;
            const pageSize = urlSize !== null ? Math.max(1, parseInt(urlSize, 10) || 10) : storePagination.pageSize;
            if (pageIndex !== storePagination.pageIndex || pageSize !== storePagination.pageSize) {
                setPagination({ pageIndex, pageSize });
            }
        } else {
            newParams.set('med_page', String(storePagination.pageIndex));
            newParams.set('med_size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        const urlFilters = [];
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('med_filter_')) {
                urlFilters.push({ id: key.replace('med_filter_', ''), value });
            }
        }
        if (urlFilters.length > 0) {
            setColumnFilters(urlFilters);
        }

        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams, storeSorting, storePagination, setSorting, setPagination, setColumnFilters]);

    const queryPage = parseInt(searchParams.get('med_page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('med_size') || String(storePagination.pageSize), 10);
    const querySortField = storeSorting.length > 0 ? storeSorting[0].id : undefined;
    const querySortDir = storeSorting.length > 0 ? (storeSorting[0].desc ? 'desc' as const : 'asc' as const) : undefined;
    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('med_filter_')) {
            queryFilters[key.replace('med_filter_', '')] = value;
        }
    }

    const utils = trpc.useUtils();

    const {
        data: medicamentsResponse,
        isLoading,
        isFetching,
    } = trpc.medicationConfig.getMedicaments.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            sortField: querySortField,
            sortDirection: querySortDir,
            filters: queryFilters,
        },
        {
            placeholderData: (prev) => prev,
        }
    );

    const createMutation = trpc.medicationConfig.createMedicament.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getMedicaments.invalidate();
            utils.medicalRecord.getMedicaments.invalidate();
            toast.success('Medikament erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen des Medikaments', { description: error.message });
        },
    });

    const updateMutation = trpc.medicationConfig.updateMedicament.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getMedicaments.invalidate();
            utils.medicalRecord.getMedicaments.invalidate();
            toast.success('Medikament erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren des Medikaments', { description: error.message });
        },
    });

    const deleteMutation = trpc.medicationConfig.deleteMedicament.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getMedicaments.invalidate();
            utils.medicalRecord.getMedicaments.invalidate();
            toast.success('Medikament erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen des Medikaments', { description: error.message });
        },
    });

    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        if (storeSorting.length > 0) {
            if (searchParams.get('med_sort') !== storeSorting[0].id) {
                newParams.set('med_sort', storeSorting[0].id);
                changed = true;
            }
            const dir = storeSorting[0].desc ? 'desc' : 'asc';
            if (searchParams.get('med_dir') !== dir) {
                newParams.set('med_dir', dir);
                changed = true;
            }
        } else {
            if (searchParams.has('med_sort')) {
                newParams.delete('med_sort');
                newParams.delete('med_dir');
                changed = true;
            }
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized.current) return;
        const currentPage = searchParams.get('med_page');
        const currentSize = searchParams.get('med_size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('med_page', storePage);
            newParams.set('med_size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;
        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('med_filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('med_filter_', ''), newParams.get(key)!));
        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach((f) => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`med_filter_${id}`);
                filtersChanged = true;
            }
        });
        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                if (value) {
                    newParams.set(`med_filter_${id}`, value);
                } else {
                    newParams.delete(`med_filter_${id}`);
                }
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, searchParams, setSearchParams]);

    const totalCount = medicamentsResponse?.totalCount ?? 0;
    const pageCount = Math.ceil(totalCount / queryPageSize);

    const table = useReactTable({
        data: (medicamentsResponse?.items ?? []) as Medicament[],
        columns: medicamentColumns,
        pageCount,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenCreate = () => {
        setSelectedMedicament(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (medicament: Medicament) => {
        setSelectedMedicament(medicament);
        setSheetOpen(true);
    };

    const handleSave = (data: { name: string; unitId?: string; regionIds: string[]; quickAccess: boolean }) => {
        if (selectedMedicament) {
            updateMutation.mutate({ id: selectedMedicament.id, ...data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleDelete = () => {
        if (selectedMedicament) {
            deleteMutation.mutate({ id: selectedMedicament.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading && !medicamentsResponse) {
        return <TableSkeleton columnCount={medicamentColumns.length} />;
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Nach Name filtern...'
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('name')?.setFilterValue(event.target.value);
                        }}
                        className='max-w-sm'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <Button onClick={handleOpenCreate}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neues Medikament
                </Button>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                                <TableHead className='w-[50px]' />
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEdit(row.original);
                                                    }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(String(row.original.id));
                                                        toast.success('ID kopiert');
                                                    }}
                                                >
                                                    ID kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={medicamentColumns.length + 1}
                                    className='h-24 text-center'
                                >
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Daten werden geladen...
                                        </div>
                                    ) : (
                                        'Keine Medikamente gefunden.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                        className='border border-gray-300 rounded px-2 py-1 text-sm'
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize} pro Seite
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {table.getState().pagination.pageIndex + 1} von{' '}
                        {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                    >
                        &lt;
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <MedicamentSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                medicament={selectedMedicament}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />
        </div>
    );
};

// ============================================
// Treatment Regions Data Table
// ============================================

const TreatmentRegionsDataTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);

    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useTreatmentRegionsTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState<TreatmentRegion | null>(null);

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        const urlSortField = searchParams.get('reg_sort');
        const urlSortDir = searchParams.get('reg_dir') || 'asc';

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
            }
        } else if (storeSorting.length > 0) {
            newParams.set('reg_sort', storeSorting[0].id);
            newParams.set('reg_dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        }

        const urlPage = searchParams.get('reg_page');
        const urlSize = searchParams.get('reg_size');

        if (urlPage !== null || urlSize !== null) {
            const pageIndex = urlPage !== null ? Math.max(0, parseInt(urlPage, 10) || 0) : storePagination.pageIndex;
            const pageSize = urlSize !== null ? Math.max(1, parseInt(urlSize, 10) || 10) : storePagination.pageSize;
            if (pageIndex !== storePagination.pageIndex || pageSize !== storePagination.pageSize) {
                setPagination({ pageIndex, pageSize });
            }
        } else {
            newParams.set('reg_page', String(storePagination.pageIndex));
            newParams.set('reg_size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        const urlFilters = [];
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('reg_filter_')) {
                urlFilters.push({ id: key.replace('reg_filter_', ''), value });
            }
        }
        if (urlFilters.length > 0) {
            setColumnFilters(urlFilters);
        }

        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams, storeSorting, storePagination, setSorting, setPagination, setColumnFilters]);

    const queryPage = parseInt(searchParams.get('reg_page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('reg_size') || String(storePagination.pageSize), 10);
    const querySortField = storeSorting.length > 0 ? storeSorting[0].id : undefined;
    const querySortDir = storeSorting.length > 0 ? (storeSorting[0].desc ? 'desc' as const : 'asc' as const) : undefined;
    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('reg_filter_')) {
            queryFilters[key.replace('reg_filter_', '')] = value;
        }
    }

    const utils = trpc.useUtils();

    const {
        data: regionsResponse,
        isLoading,
        isFetching,
    } = trpc.medicationConfig.getTreatmentRegions.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            sortField: querySortField,
            sortDirection: querySortDir,
            filters: queryFilters,
        },
        {
            placeholderData: (prev) => prev,
        }
    );

    const createMutation = trpc.medicationConfig.createTreatmentRegion.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTreatmentRegions.invalidate();
            utils.medicalRecord.getTreatmentRegions.invalidate();
            toast.success('Behandlungszone erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen der Behandlungszone', { description: error.message });
        },
    });

    const updateMutation = trpc.medicationConfig.updateTreatmentRegion.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTreatmentRegions.invalidate();
            utils.medicalRecord.getTreatmentRegions.invalidate();
            toast.success('Behandlungszone erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren der Behandlungszone', { description: error.message });
        },
    });

    const deleteMutation = trpc.medicationConfig.deleteTreatmentRegion.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTreatmentRegions.invalidate();
            utils.medicalRecord.getTreatmentRegions.invalidate();
            toast.success('Behandlungszone erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen der Behandlungszone', { description: error.message });
        },
    });

    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        if (storeSorting.length > 0) {
            if (searchParams.get('reg_sort') !== storeSorting[0].id) {
                newParams.set('reg_sort', storeSorting[0].id);
                changed = true;
            }
            const dir = storeSorting[0].desc ? 'desc' : 'asc';
            if (searchParams.get('reg_dir') !== dir) {
                newParams.set('reg_dir', dir);
                changed = true;
            }
        } else {
            if (searchParams.has('reg_sort')) {
                newParams.delete('reg_sort');
                newParams.delete('reg_dir');
                changed = true;
            }
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized.current) return;
        const currentPage = searchParams.get('reg_page');
        const currentSize = searchParams.get('reg_size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('reg_page', storePage);
            newParams.set('reg_size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;
        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('reg_filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('reg_filter_', ''), newParams.get(key)!));
        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach((f) => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`reg_filter_${id}`);
                filtersChanged = true;
            }
        });
        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                if (value) {
                    newParams.set(`reg_filter_${id}`, value);
                } else {
                    newParams.delete(`reg_filter_${id}`);
                }
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, searchParams, setSearchParams]);

    const totalCount = regionsResponse?.totalCount ?? 0;
    const pageCount = Math.ceil(totalCount / queryPageSize);

    const table = useReactTable({
        data: (regionsResponse?.items ?? []) as TreatmentRegion[],
        columns: regionColumns,
        pageCount,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenCreate = () => {
        setSelectedRegion(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (region: TreatmentRegion) => {
        setSelectedRegion(region);
        setSheetOpen(true);
    };

    const handleSave = (data: { name: string }) => {
        if (selectedRegion) {
            updateMutation.mutate({ id: selectedRegion.id, ...data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleDelete = () => {
        if (selectedRegion) {
            deleteMutation.mutate({ id: selectedRegion.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading && !regionsResponse) {
        return <TableSkeleton columnCount={regionColumns.length} />;
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Nach Name filtern...'
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('name')?.setFilterValue(event.target.value);
                        }}
                        className='max-w-sm'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <Button onClick={handleOpenCreate}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neue Behandlungszone
                </Button>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                                <TableHead className='w-[50px]' />
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEdit(row.original);
                                                    }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(String(row.original.id));
                                                        toast.success('ID kopiert');
                                                    }}
                                                >
                                                    ID kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={regionColumns.length + 1}
                                    className='h-24 text-center'
                                >
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Daten werden geladen...
                                        </div>
                                    ) : (
                                        'Keine Behandlungszonen gefunden.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                        className='border border-gray-300 rounded px-2 py-1 text-sm'
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize} pro Seite
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {table.getState().pagination.pageIndex + 1} von{' '}
                        {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                    >
                        &lt;
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <TreatmentRegionSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                region={selectedRegion}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />
        </div>
    );
};

// ============================================
// Treatment Text Snippets Types
// ============================================

type TreatmentTextSnippet = {
    id: string;
    name: string;
    content: string;
    categoryId: string | null;
    category: { id: string; name: string } | null;
    stores?: { storeId: number }[];
    createdAt: string;
    updatedAt: string;
};

type TextCategory = {
    id: string;
    name: string;
    color: string | null;
};

const categoryColorPresets = [
    "#FFFC92", "#E1E97B", "#BDD7A5", "#DEC8E2", "#B5CFE8",
    "#BBDACB", "#fca16f", "#FBE4B2", "#fc626f", "#B3FCF3", "#FC03DF",
];

// ============================================
// Category Management Sheet
// ============================================

const CategoryManagementSheet = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryColor, setNewCategoryColor] = useState<string | null>(null);
    const [editingCategory, setEditingCategory] = useState<TextCategory | null>(null);
    const [editName, setEditName] = useState('');
    const [editColor, setEditColor] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<TextCategory | null>(null);

    const utils = trpc.useUtils();

    const { data: categories = [] } = trpc.medicationConfig.getTextCategories.useQuery();

    const createMutation = trpc.medicationConfig.createTextCategory.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTextCategories.invalidate();
            utils.medicationConfig.getTreatmentTexts.invalidate();
            toast.success('Kategorie erstellt');
            setNewCategoryName('');
            setNewCategoryColor(null);
        },
        onError: (error) => toast.error('Fehler', { description: error.message }),
    });

    const updateMutation = trpc.medicationConfig.updateTextCategory.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTextCategories.invalidate();
            utils.medicationConfig.getTreatmentTexts.invalidate();
            toast.success('Kategorie aktualisiert');
            setEditingCategory(null);
        },
        onError: (error) => toast.error('Fehler', { description: error.message }),
    });

    const deleteMutation = trpc.medicationConfig.deleteTextCategory.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTextCategories.invalidate();
            utils.medicationConfig.getTreatmentTexts.invalidate();
            toast.success('Kategorie gelöscht');
            setCategoryToDelete(null);
        },
        onError: (error) => toast.error('Fehler', { description: error.message }),
    });

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        if (newCategoryName.trim()) {
            createMutation.mutate({ name: newCategoryName.trim(), color: newCategoryColor ?? undefined });
        }
    };

    const handleUpdate = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingCategory && editName.trim()) {
            updateMutation.mutate({ id: editingCategory.id, name: editName.trim(), color: editColor });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <SheetHeader>
                        <SheetTitle>Kategorien verwalten</SheetTitle>
                        <SheetDescription>
                            Erstellen, bearbeiten oder löschen Sie Kategorien für Doku-Snippets.
                        </SheetDescription>
                    </SheetHeader>
                    <div className='flex flex-col gap-6 py-4 px-4'>
                        <form onSubmit={handleCreate} className='flex flex-col gap-2'>
                            <div className='flex gap-2'>
                                <Input
                                    value={newCategoryName}
                                    onChange={(e) => setNewCategoryName(e.target.value)}
                                    placeholder='Neue Kategorie...'
                                    className='flex-1'
                                />
                                <Button type='submit' size='sm' disabled={!newCategoryName.trim() || createMutation.isPending}>
                                    <Plus className='h-4 w-4' />
                                </Button>
                            </div>
                            <div className='flex items-center gap-1.5 flex-wrap'>
                                {categoryColorPresets.map((preset) => (
                                    <button
                                        key={preset}
                                        type='button'
                                        className={`h-6 w-6 rounded-full border-2 transition-all ${newCategoryColor === preset ? 'ring-2 ring-primary ring-offset-1 border-primary' : 'border-transparent hover:scale-110'}`}
                                        style={{ backgroundColor: preset }}
                                        onClick={() => setNewCategoryColor(newCategoryColor === preset ? null : preset)}
                                    />
                                ))}
                            </div>
                        </form>

                        <div className='flex flex-col gap-2'>
                            {categories.map((cat) => (
                                <div key={cat.id} className='flex flex-col gap-2 p-2 rounded border'>
                                    {editingCategory?.id === cat.id ? (
                                        <form onSubmit={handleUpdate} className='flex flex-col gap-2'>
                                            <div className='flex gap-2'>
                                                <Input
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    className='flex-1'
                                                    autoFocus
                                                />
                                                <Button type='submit' size='sm' disabled={!editName.trim()}>
                                                    Speichern
                                                </Button>
                                                <Button type='button' size='sm' variant='ghost' onClick={() => setEditingCategory(null)}>
                                                    Abbrechen
                                                </Button>
                                            </div>
                                            <div className='flex items-center gap-1.5 flex-wrap'>
                                                {categoryColorPresets.map((preset) => (
                                                    <button
                                                        key={preset}
                                                        type='button'
                                                        className={`h-5 w-5 rounded-full border-2 transition-all ${editColor === preset ? 'ring-2 ring-primary ring-offset-1 border-primary' : 'border-transparent hover:scale-110'}`}
                                                        style={{ backgroundColor: preset }}
                                                        onClick={() => setEditColor(editColor === preset ? null : preset)}
                                                    />
                                                ))}
                                            </div>
                                        </form>
                                    ) : (
                                        <div className='flex items-center gap-2'>
                                            {cat.color && (
                                                <span className='h-4 w-4 rounded-full shrink-0' style={{ backgroundColor: cat.color }} />
                                            )}
                                            <span className='flex-1 text-sm'>{cat.name}</span>
                                            <Button
                                                size='sm'
                                                variant='ghost'
                                                onClick={() => { setEditingCategory(cat as TextCategory); setEditName(cat.name); setEditColor((cat as TextCategory).color ?? null); }}
                                            >
                                                <Pencil className='h-3 w-3' />
                                            </Button>
                                            <Button
                                                size='sm'
                                                variant='ghost'
                                                onClick={() => { setCategoryToDelete(cat as TextCategory); setShowDeleteDialog(true); }}
                                            >
                                                <Trash2 className='h-3 w-3' />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {categories.length === 0 && (
                                <p className='text-sm text-muted-foreground text-center py-4'>
                                    Noch keine Kategorien vorhanden.
                                </p>
                            )}
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie die Kategorie &quot;{categoryToDelete?.name}&quot; wirklich löschen?
                            Snippets in dieser Kategorie werden keiner Kategorie mehr zugeordnet.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (categoryToDelete) deleteMutation.mutate({ id: categoryToDelete.id });
                                setShowDeleteDialog(false);
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ============================================
// Treatment Text Snippet Sheet
// ============================================

interface SnippetSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    snippet: TreatmentTextSnippet | null;
    onSave: (data: { name: string; content: string; categoryId: string | null; storeIds: number[] }) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const SnippetSheet = ({ open, onOpenChange, snippet, onSave, onDelete, isSaving }: SnippetSheetProps) => {
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [categoryId, setCategoryId] = useState<string | null>(null);
    const [selectedStoreIds, setSelectedStoreIds] = useState<Set<number>>(new Set());
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const contentEditorRef = useRef<PlateEditorRef>(null);

    const { data: categories = [] } = trpc.medicationConfig.getTextCategories.useQuery();
    const { data: storesData } = trpc.stores.getAll.useQuery({ filters: {} });
    const stores = useMemo(() => storesData?.data ?? [], [storesData?.data]);

    useEffect(() => {
        if (snippet) {
            setName(snippet.name);
            setContent(snippet.content);
            setCategoryId(snippet.categoryId);
            setSelectedStoreIds(new Set(snippet.stores?.map(s => s.storeId) ?? []));
        } else {
            setName('');
            setContent('');
            setCategoryId(null);
            setSelectedStoreIds(new Set(stores.map(s => s.id)));
        }
    }, [snippet, open, stores]);

    const toggleStore = (storeId: number) => {
        setSelectedStoreIds(prev => {
            const next = new Set(prev);
            if (next.has(storeId)) {
                next.delete(storeId);
            } else {
                next.add(storeId);
            }
            return next;
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const editorContent = contentEditorRef.current?.getHtml() ?? '';
        if (name.trim() && editorContent.trim()) {
            onSave({ name: name.trim(), content: editorContent, categoryId, storeIds: Array.from(selectedStoreIds) });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto sm:max-w-lg'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {snippet ? 'Doku-Snippet bearbeiten' : 'Neues Doku-Snippet'}
                            </SheetTitle>
                            <SheetDescription>
                                {snippet
                                    ? 'Bearbeiten Sie den Text-Baustein.'
                                    : 'Erstellen Sie einen neuen Text-Baustein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='snippet-name'>Name</Label>
                                <Input
                                    id='snippet-name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='z.B. Aufklärung Hyaluron'
                                    autoFocus
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='snippet-category'>Kategorie</Label>
                                <Select
                                    value={categoryId ?? '__none__'}
                                    onValueChange={(val) => setCategoryId(val === '__none__' ? null : val)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder='Keine Kategorie' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='__none__'>Keine Kategorie</SelectItem>
                                        {categories.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label>Standorte</Label>
                                <div className='flex flex-col gap-2'>
                                    {stores.map((store) => (
                                        <label key={store.id} className='flex items-center gap-2 cursor-pointer'>
                                            <Checkbox
                                                checked={selectedStoreIds.has(store.id)}
                                                onCheckedChange={() => toggleStore(store.id)}
                                            />
                                            <span className='text-sm'>{store.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label>Inhalt</Label>
                                <PlateEditor
                                    ref={contentEditorRef}
                                    initialValue={content}
                                    placeholder='Text-Baustein Inhalt...'
                                />
                            </div>
                        </div>
                        <SheetFooter>
                            {snippet && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim()}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {snippet ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Doku-Snippet löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie das Snippet &quot;{snippet?.name}&quot; wirklich löschen? Diese Aktion
                            kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ============================================
// Snippet Order Sheet
// ============================================

const SnippetOrderSheet = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => {
    const { data: allSnippets } = trpc.medicalRecord.getTreatmentTextSnippets.useQuery(undefined, { enabled: open });
    const { data: categories = [] } = trpc.medicationConfig.getTextCategories.useQuery(undefined, { enabled: open });
    const [localOrder, setLocalOrder] = useState<Record<string, string[]>>({});
    const [dirtyCategories, setDirtyCategories] = useState<Set<string>>(new Set());

    const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
    const [dirtyCategoryOrder, setDirtyCategoryOrder] = useState(false);

    const utils = trpc.useUtils();

    useEffect(() => {
        if (allSnippets && categories.length > 0) {
            const grouped: Record<string, string[]> = {};
            for (const cat of categories) {
                const catSnippets = allSnippets
                    .filter((s) => s.categoryId === cat.id)
                    .sort((a, b) => (a.categoryOrder ?? 0) - (b.categoryOrder ?? 0));
                grouped[cat.id] = catSnippets.map((s) => s.id);
            }
            setLocalOrder(grouped);
            setDirtyCategories(new Set());
        }
    }, [allSnippets, categories]);

    useEffect(() => {
        if (categories.length > 0) {
            setCategoryOrder(categories.map((c) => c.id));
            setDirtyCategoryOrder(false);
        }
    }, [categories]);

    const moveCategoryItem = (fromIndex: number, toIndex: number) => {
        setCategoryOrder((prev) => {
            const arr = [...prev];
            const [item] = arr.splice(fromIndex, 1);
            arr.splice(toIndex, 0, item);
            return arr;
        });
        setDirtyCategoryOrder(true);
    };

    const moveItem = (categoryId: string, fromIndex: number, toIndex: number) => {
        setLocalOrder((prev) => {
            const arr = [...(prev[categoryId] ?? [])];
            const [item] = arr.splice(fromIndex, 1);
            arr.splice(toIndex, 0, item);
            return { ...prev, [categoryId]: arr };
        });
        setDirtyCategories((prev) => new Set(prev).add(categoryId));
    };

    const reorderMutation = trpc.medicationConfig.reorderSnippetsInCategory.useMutation({
        onSuccess: () => {
            utils.medicalRecord.getTreatmentTextSnippets.invalidate();
            utils.medicationConfig.getTreatmentTexts.invalidate();
        },
    });

    const reorderCategoriesMutation = trpc.medicationConfig.reorderCategories.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTextCategories.invalidate();
        },
    });

    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (dirtyCategoryOrder) {
                await reorderCategoriesMutation.mutateAsync({ orderedIds: categoryOrder });
            }
            for (const categoryId of dirtyCategories) {
                const orderedIds = localOrder[categoryId];
                if (orderedIds) {
                    await reorderMutation.mutateAsync({ orderedIds });
                }
            }
            toast.success('Reihenfolge gespeichert');
            setDirtyCategories(new Set());
            setDirtyCategoryOrder(false);
            onOpenChange(false);
        } catch (error) {
            toast.error('Fehler beim Speichern der Reihenfolge');
        } finally {
            setIsSaving(false);
        }
    };

    const isDirty = dirtyCategories.size > 0 || dirtyCategoryOrder;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='overflow-y-auto sm:max-w-lg'>
                <SheetHeader>
                    <SheetTitle>Reihenfolge</SheetTitle>
                    <SheetDescription>
                        Ordnen Sie Kategorien und Textbausteine mit den Pfeilen.
                    </SheetDescription>
                </SheetHeader>
                <div className='flex flex-col gap-6 py-4 px-4'>
                    {categories.length > 0 && (
                        <div>
                            <h4 className='text-sm font-semibold mb-2'>Kategorien-Reihenfolge</h4>
                            <div className='flex flex-col gap-1 border rounded-md p-2'>
                                {categoryOrder.map((catId, index) => {
                                    const cat = categories.find((c) => c.id === catId);
                                    if (!cat) return null;
                                    return (
                                        <div key={catId} className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50'>
                                            <span className='flex-1 text-sm truncate'>{cat.name}</span>
                                            <Button
                                                type='button'
                                                size='sm'
                                                variant='ghost'
                                                className='h-7 w-7 p-0'
                                                disabled={index === 0 || isSaving}
                                                onClick={() => moveCategoryItem(index, index - 1)}
                                            >
                                                <ArrowUp className='h-3.5 w-3.5' />
                                            </Button>
                                            <Button
                                                type='button'
                                                size='sm'
                                                variant='ghost'
                                                className='h-7 w-7 p-0'
                                                disabled={index === categoryOrder.length - 1 || isSaving}
                                                onClick={() => moveCategoryItem(index, index + 1)}
                                            >
                                                <ArrowDown className='h-3.5 w-3.5' />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <Separator />

                    {categories.map((cat) => {
                        const ids = localOrder[cat.id] ?? [];
                        if (ids.length === 0) return null;
                        return (
                            <div key={cat.id}>
                                <h4 className='text-sm font-semibold mb-2'>{cat.name}</h4>
                                <div className='flex flex-col gap-1 border rounded-md p-2'>
                                    {ids.map((id, index) => {
                                        const snippet = allSnippets?.find((s) => s.id === id);
                                        if (!snippet) return null;
                                        return (
                                            <div key={id} className='flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50'>
                                                <span className='flex-1 text-sm truncate'>{snippet.name}</span>
                                                <Button
                                                    type='button'
                                                    size='sm'
                                                    variant='ghost'
                                                    className='h-7 w-7 p-0'
                                                    disabled={index === 0 || isSaving}
                                                    onClick={() => moveItem(cat.id, index, index - 1)}
                                                >
                                                    <ArrowUp className='h-3.5 w-3.5' />
                                                </Button>
                                                <Button
                                                    type='button'
                                                    size='sm'
                                                    variant='ghost'
                                                    className='h-7 w-7 p-0'
                                                    disabled={index === ids.length - 1 || isSaving}
                                                    onClick={() => moveItem(cat.id, index, index + 1)}
                                                >
                                                    <ArrowDown className='h-3.5 w-3.5' />
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                    {categories.length === 0 && (
                        <p className='text-sm text-muted-foreground text-center py-4'>
                            Keine Kategorien vorhanden.
                        </p>
                    )}
                </div>
                <SheetFooter>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving || !isDirty}
                    >
                        {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                        Speichern
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

// ============================================
// Treatment Text Snippets Data Table
// ============================================

const snippetColumns: ColumnDef<TreatmentTextSnippet>[] = [
    {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column} label='Name' />,
        enableSorting: true,
    },
    {
        accessorKey: 'category',
        header: 'Kategorie',
        cell: ({ row }) => {
            const category = row.original.category;
            return category ? <Badge variant='secondary'>{category.name}</Badge> : <span className='text-muted-foreground'>—</span>;
        },
    },
    {
        accessorKey: 'content',
        header: 'Inhalt',
        cell: ({ row }) => {
            const content = row.original.content;
            return <span className='text-sm text-muted-foreground line-clamp-1'>{content}</span>;
        },
    },
];

const TreatmentTextSnippetsDataTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [sheetOpen, setSheetOpen] = useState(false);
    const [categorySheetOpen, setCategorySheetOpen] = useState(false);
    const [orderSheetOpen, setOrderSheetOpen] = useState(false);
    const [selectedSnippet, setSelectedSnippet] = useState<TreatmentTextSnippet | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [nameFilter, setNameFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');

    const utils = trpc.useUtils();
    const { data: categories = [] } = trpc.medicationConfig.getTextCategories.useQuery();

    const filters: Record<string, string | undefined> = {};
    if (nameFilter) filters.name = nameFilter;
    if (categoryFilter) filters.categoryId = categoryFilter;

    const {
        data: snippetsResponse,
        isLoading,
        isFetching,
    } = trpc.medicationConfig.getTreatmentTexts.useQuery(
        { page, pageSize, sortField: 'sequence', sortDirection: 'asc', filters },
        { placeholderData: (prev) => prev }
    );

    const createMutation = trpc.medicationConfig.createTreatmentText.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTreatmentTexts.invalidate();
            toast.success('Doku-Snippet erstellt');
            setSheetOpen(false);
        },
        onError: (error) => toast.error('Fehler beim Erstellen', { description: error.message }),
    });

    const updateMutation = trpc.medicationConfig.updateTreatmentText.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTreatmentTexts.invalidate();
            toast.success('Doku-Snippet aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => toast.error('Fehler beim Aktualisieren', { description: error.message }),
    });

    const deleteMutation = trpc.medicationConfig.deleteTreatmentText.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getTreatmentTexts.invalidate();
            toast.success('Doku-Snippet gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => toast.error('Fehler beim Löschen', { description: error.message }),
    });

    const handleOpenCreate = () => {
        setSelectedSnippet(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (snippet: TreatmentTextSnippet) => {
        setSelectedSnippet(snippet);
        setSheetOpen(true);
    };

    const handleSave = (data: { name: string; content: string; categoryId: string | null; storeIds: number[] }) => {
        if (selectedSnippet) {
            updateMutation.mutate({ id: selectedSnippet.id, ...data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleDelete = () => {
        if (selectedSnippet) {
            deleteMutation.mutate({ id: selectedSnippet.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
    const totalCount = snippetsResponse?.totalCount ?? 0;
    const pageCount = Math.ceil(totalCount / pageSize);
    const items = (snippetsResponse?.items ?? []) as TreatmentTextSnippet[];

    if (isLoading && !snippetsResponse) {
        return <TableSkeleton columnCount={3} />;
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Nach Name filtern...'
                        value={nameFilter}
                        onChange={(e) => { setNameFilter(e.target.value); setPage(0); }}
                        className='max-w-sm'
                    />
                    <Select
                        value={categoryFilter || '__all__'}
                        onValueChange={(val) => { setCategoryFilter(val === '__all__' ? '' : val); setPage(0); }}
                    >
                        <SelectTrigger className='w-[180px]'>
                            <SelectValue placeholder='Alle Kategorien' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='__all__'>Alle Kategorien</SelectItem>
                            {categories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <div className='flex gap-2'>
                    <Button variant='outline' onClick={() => setOrderSheetOpen(true)}>
                        Sortieren
                    </Button>
                    <Button variant='outline' onClick={() => setCategorySheetOpen(true)}>
                        Kategorien
                    </Button>
                    <Button onClick={handleOpenCreate}>
                        <Plus className='mr-2 h-4 w-4' />
                        Neues Snippet
                    </Button>
                </div>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        <TableRow>
                            <TableHead className='font-medium text-gray-700'>Name</TableHead>
                            <TableHead className='font-medium text-gray-700'>Kategorie</TableHead>
                            <TableHead className='font-medium text-gray-700'>Inhalt</TableHead>
                            <TableHead className='w-[50px]' />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.length > 0 ? (
                            items.map((snippet) => (
                                <TableRow
                                    key={snippet.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(snippet)}
                                >
                                    <TableCell className='font-medium'>{snippet.name}</TableCell>
                                    <TableCell>
                                        {snippet.category ? (
                                            <Badge variant='secondary'>{snippet.category.name}</Badge>
                                        ) : (
                                            <span className='text-muted-foreground'>—</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <span className='text-sm text-muted-foreground line-clamp-1 max-w-[300px] block'>
                                            {snippet.content}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => { e.stopPropagation(); handleOpenEdit(snippet); }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(snippet.id);
                                                        toast.success('ID kopiert');
                                                    }}
                                                >
                                                    ID kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} className='h-24 text-center'>
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Daten werden geladen...
                                        </div>
                                    ) : (
                                        'Keine Doku-Snippets gefunden.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                        className='border border-gray-300 rounded px-2 py-1 text-sm'
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map((size) => (
                            <option key={size} value={size}>{size} pro Seite</option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {page + 1} von {pageCount > 0 ? pageCount : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0 || isFetching}
                    >
                        &lt;
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page + 1 >= pageCount || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <SnippetSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                snippet={selectedSnippet}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />

            <CategoryManagementSheet
                open={categorySheetOpen}
                onOpenChange={setCategorySheetOpen}
            />

            <SnippetOrderSheet
                open={orderSheetOpen}
                onOpenChange={setOrderSheetOpen}
            />
        </div>
    );
};

// ============================================
// Unit Sheet
// ============================================

interface UnitSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    unit: MedicamentUnit | null;
    onSave: (data: { name: string }) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const UnitSheet = ({ open, onOpenChange, unit, onSave, onDelete, isSaving }: UnitSheetProps) => {
    const [name, setName] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    useEffect(() => {
        if (unit) {
            setName(unit.name);
        } else {
            setName('');
        }
    }, [unit, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim()) {
            onSave({ name: name.trim() });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {unit ? 'Einheit bearbeiten' : 'Neue Einheit'}
                            </SheetTitle>
                            <SheetDescription>
                                {unit
                                    ? 'Bearbeiten Sie den Namen der Einheit.'
                                    : 'Geben Sie den Namen für die neue Einheit ein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='unit-name'>Name</Label>
                                <Input
                                    id='unit-name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='z.B. ml, IE, mg'
                                    autoFocus
                                />
                            </div>
                        </div>
                        <SheetFooter>
                            {unit && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim()}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {unit ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Einheit löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie die Einheit &quot;{unit?.name}&quot; wirklich löschen? Diese Aktion
                            kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ============================================
// Units Data Table
// ============================================

const UnitsDataTable = () => {
    const [page, setPage] = useState(0);
    const [pageSize] = useState(10);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [nameFilter, setNameFilter] = useState('');
    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState<MedicamentUnit | null>(null);

    const utils = trpc.useUtils();

    const querySortField = sorting.length > 0 ? sorting[0].id : undefined;
    const querySortDir = sorting.length > 0 ? (sorting[0].desc ? 'desc' as const : 'asc' as const) : undefined;

    const {
        data: unitsResponse,
        isLoading,
        isFetching,
    } = trpc.medicationConfig.getUnits.useQuery(
        {
            page,
            pageSize,
            sortField: querySortField,
            sortDirection: querySortDir,
            filters: { name: nameFilter || undefined },
        },
        { placeholderData: (prev) => prev }
    );

    const createMutation = trpc.medicationConfig.createUnit.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getUnits.invalidate();
            utils.medicationConfig.getAllUnits.invalidate();
            toast.success('Einheit erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen der Einheit', { description: error.message });
        },
    });

    const updateMutation = trpc.medicationConfig.updateUnit.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getUnits.invalidate();
            utils.medicationConfig.getAllUnits.invalidate();
            toast.success('Einheit erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren der Einheit', { description: error.message });
        },
    });

    const deleteMutation = trpc.medicationConfig.deleteUnit.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getUnits.invalidate();
            utils.medicationConfig.getAllUnits.invalidate();
            toast.success('Einheit erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen der Einheit', { description: error.message });
        },
    });

    const totalCount = unitsResponse?.totalCount ?? 0;
    const pageCount = Math.ceil(totalCount / pageSize);

    const table = useReactTable({
        data: (unitsResponse?.items ?? []) as MedicamentUnit[],
        columns: unitColumns,
        pageCount,
        state: { sorting, pagination: { pageIndex: page, pageSize } },
        onSortingChange: setSorting,
        onPaginationChange: (updater) => {
            const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize }) : updater;
            setPage(next.pageIndex);
        },
        manualPagination: true,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenCreate = () => {
        setSelectedUnit(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (unit: MedicamentUnit) => {
        setSelectedUnit(unit);
        setSheetOpen(true);
    };

    const handleSave = (data: { name: string }) => {
        if (selectedUnit) {
            updateMutation.mutate({ id: selectedUnit.id, ...data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleDelete = () => {
        if (selectedUnit) {
            deleteMutation.mutate({ id: selectedUnit.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading && !unitsResponse) {
        return <TableSkeleton columnCount={unitColumns.length} />;
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Nach Name filtern...'
                        value={nameFilter}
                        onChange={(event) => {
                            setNameFilter(event.target.value);
                            setPage(0);
                        }}
                        className='max-w-sm'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <Button onClick={handleOpenCreate}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neue Einheit
                </Button>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                                <TableHead className='w-[50px]' />
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEdit(row.original);
                                                    }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(String(row.original.id));
                                                        toast.success('ID kopiert');
                                                    }}
                                                >
                                                    ID kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={unitColumns.length + 1} className='h-24 text-center'>
                                    Keine Einheiten gefunden.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4'>
                <div className='text-sm text-muted-foreground'>
                    {totalCount} Einheit{totalCount !== 1 ? 'en' : ''} gesamt
                </div>
                <div className='flex items-center space-x-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0 || isFetching}
                    >
                        &lt;
                    </Button>
                    <span className='text-sm'>
                        Seite {page + 1} von {Math.max(1, pageCount)}
                    </span>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page + 1 >= pageCount || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <UnitSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                unit={selectedUnit}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />
        </div>
    );
};

// ============================================
// ICD Codes
// ============================================

type IcdCode = {
    id: string;
    name: string;
    icdNumber: string;
    description: string;
    position: number;
};

const icdCodeColumns: ColumnDef<IcdCode>[] = [
    {
        accessorKey: 'icdNumber',
        header: ({ column }) => (
            <Button
                variant='ghost'
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
                ICD-Nummer
                {column.getIsSorted() === 'asc' ? <ArrowUp className='ml-2 h-4 w-4' /> :
                    column.getIsSorted() === 'desc' ? <ArrowDown className='ml-2 h-4 w-4' /> :
                        <ArrowUpDown className='ml-2 h-4 w-4' />}
            </Button>
        ),
    },
    {
        accessorKey: 'name',
        header: ({ column }) => (
            <Button
                variant='ghost'
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
                Name
                {column.getIsSorted() === 'asc' ? <ArrowUp className='ml-2 h-4 w-4' /> :
                    column.getIsSorted() === 'desc' ? <ArrowDown className='ml-2 h-4 w-4' /> :
                        <ArrowUpDown className='ml-2 h-4 w-4' />}
            </Button>
        ),
    },
    {
        accessorKey: 'description',
        header: 'Beschreibung',
        cell: ({ row }) => (
            <span className='text-muted-foreground truncate max-w-[200px] block'>
                {row.original.description || '-'}
            </span>
        ),
    },
];

interface IcdCodeSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    icdCode: IcdCode | null;
    onSave: (data: { name: string; icdNumber: string; description: string }) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const IcdCodeSheet = ({ open, onOpenChange, icdCode, onSave, onDelete, isSaving }: IcdCodeSheetProps) => {
    const [name, setName] = useState('');
    const [icdNumber, setIcdNumber] = useState('');
    const [description, setDescription] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    useEffect(() => {
        if (icdCode) {
            setName(icdCode.name);
            setIcdNumber(icdCode.icdNumber);
            setDescription(icdCode.description);
        } else {
            setName('');
            setIcdNumber('');
            setDescription('');
        }
    }, [icdCode, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim() && icdNumber.trim()) {
            onSave({ name: name.trim(), icdNumber: icdNumber.trim(), description: description.trim() });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {icdCode ? 'ICD-Code bearbeiten' : 'Neuer ICD-Code'}
                            </SheetTitle>
                            <SheetDescription>
                                {icdCode
                                    ? 'Bearbeiten Sie den ICD-Code.'
                                    : 'Erstellen Sie einen neuen ICD-Code.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='icd-number'>ICD-Nummer</Label>
                                <Input
                                    id='icd-number'
                                    value={icdNumber}
                                    onChange={(e) => setIcdNumber(e.target.value)}
                                    placeholder='z.B. A01.0'
                                    autoFocus
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='icd-name'>Name</Label>
                                <Input
                                    id='icd-name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='z.B. Enterotyphus'
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='icd-description'>Beschreibung</Label>
                                <Textarea
                                    id='icd-description'
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder='Optionale Beschreibung...'
                                    rows={3}
                                />
                            </div>
                        </div>
                        <SheetFooter>
                            {icdCode && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim() || !icdNumber.trim()}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {icdCode ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>ICD-Code löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie den ICD-Code &quot;{icdCode?.icdNumber} {icdCode?.name}&quot; wirklich löschen?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-white hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

const IcdCodesDataTable = () => {
    const [page, setPage] = useState(0);
    const [pageSize] = useState(20);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [searchFilter, setSearchFilter] = useState('');
    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedIcdCode, setSelectedIcdCode] = useState<IcdCode | null>(null);

    const utils = trpc.useUtils();

    const querySortField = sorting.length > 0 ? sorting[0].id : undefined;
    const querySortDir = sorting.length > 0 ? (sorting[0].desc ? 'desc' as const : 'asc' as const) : undefined;

    const {
        data: icdResponse,
        isLoading,
        isFetching,
    } = trpc.medicationConfig.getIcdCodes.useQuery(
        {
            page,
            pageSize,
            sortField: querySortField,
            sortDirection: querySortDir,
            filters: { search: searchFilter || undefined },
        },
        { placeholderData: (prev) => prev }
    );

    const createMutation = trpc.medicationConfig.createIcdCode.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getIcdCodes.invalidate();
            toast.success('ICD-Code erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => toast.error('Fehler beim Erstellen', { description: error.message }),
    });

    const updateMutation = trpc.medicationConfig.updateIcdCode.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getIcdCodes.invalidate();
            toast.success('ICD-Code erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => toast.error('Fehler beim Aktualisieren', { description: error.message }),
    });

    const deleteMutation = trpc.medicationConfig.deleteIcdCode.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getIcdCodes.invalidate();
            toast.success('ICD-Code erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => toast.error('Fehler beim Löschen', { description: error.message }),
    });

    const totalCount = icdResponse?.totalCount ?? 0;
    const pageCount = Math.ceil(totalCount / pageSize);

    const table = useReactTable({
        data: (icdResponse?.items ?? []) as IcdCode[],
        columns: icdCodeColumns,
        pageCount,
        state: { sorting, pagination: { pageIndex: page, pageSize } },
        onSortingChange: setSorting,
        onPaginationChange: (updater) => {
            const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize }) : updater;
            setPage(next.pageIndex);
        },
        manualPagination: true,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenCreate = () => {
        setSelectedIcdCode(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (icd: IcdCode) => {
        setSelectedIcdCode(icd);
        setSheetOpen(true);
    };

    const handleSave = (data: { name: string; icdNumber: string; description: string }) => {
        if (selectedIcdCode) {
            updateMutation.mutate({ id: selectedIcdCode.id, ...data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleDelete = () => {
        if (selectedIcdCode) {
            deleteMutation.mutate({ id: selectedIcdCode.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading && !icdResponse) {
        return <TableSkeleton columnCount={icdCodeColumns.length} />;
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='ICD-Code oder Name suchen...'
                        value={searchFilter}
                        onChange={(event) => {
                            setSearchFilter(event.target.value);
                            setPage(0);
                        }}
                        className='max-w-sm'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <Button onClick={handleOpenCreate}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neuer ICD-Code
                </Button>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                                <TableHead className='w-[50px]' />
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEdit(row.original);
                                                    }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(row.original.icdNumber);
                                                        toast.success('ICD-Nummer kopiert');
                                                    }}
                                                >
                                                    ICD-Nummer kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={icdCodeColumns.length + 1} className='h-24 text-center'>
                                    Keine ICD-Codes gefunden.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4'>
                <div className='text-sm text-muted-foreground'>
                    {totalCount} ICD-Code{totalCount !== 1 ? 's' : ''} gesamt
                </div>
                <div className='flex items-center space-x-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0 || isFetching}
                    >
                        &lt;
                    </Button>
                    <span className='text-sm'>
                        Seite {page + 1} von {Math.max(1, pageCount)}
                    </span>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page + 1 >= pageCount || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <IcdCodeSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                icdCode={selectedIcdCode}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />
        </div>
    );
};

// ============================================
// Consent Pairs (Einwilligung-Snippets)
// ============================================

type ConsentPair = {
    id: string;
    name: string;
    required: boolean;
    onlyRequiredForFemales: boolean;
    positiveText: { id: string; name: string; content: string };
    negativeText: { id: string; name: string; content: string };
};

interface ConsentPairSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    pair: ConsentPair | null;
    onSave: (data: { name: string; positiveTextId: string; negativeTextId: string; required: boolean; onlyRequiredForFemales: boolean }) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const ConsentPairSheet = ({ open, onOpenChange, pair, onSave, onDelete, isSaving }: ConsentPairSheetProps) => {
    const [name, setName] = useState('');
    const [positiveTextId, setPositiveTextId] = useState('');
    const [negativeTextId, setNegativeTextId] = useState('');
    const [required, setRequired] = useState(true);
    const [onlyRequiredForFemales, setOnlyRequiredForFemales] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const { data: allSnippets } = trpc.medicalRecord.getTreatmentTextSnippets.useQuery(undefined, { enabled: open });

    useEffect(() => {
        if (pair) {
            setName(pair.name);
            setPositiveTextId(pair.positiveText.id);
            setNegativeTextId(pair.negativeText.id);
            setRequired(pair.required);
            setOnlyRequiredForFemales(pair.onlyRequiredForFemales);
        } else {
            setName('');
            setPositiveTextId('');
            setNegativeTextId('');
            setRequired(true);
            setOnlyRequiredForFemales(false);
        }
    }, [pair, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim() && positiveTextId && negativeTextId) {
            onSave({ name: name.trim(), positiveTextId, negativeTextId, required, onlyRequiredForFemales });
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {pair ? 'Einwilligung bearbeiten' : 'Neue Einwilligung'}
                            </SheetTitle>
                            <SheetDescription>
                                {pair
                                    ? 'Bearbeiten Sie die Einwilligungs-Zuordnung.'
                                    : 'Erstellen Sie eine neue Einwilligungs-Zuordnung mit positivem und negativem Textbaustein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='consent-pair-name'>Name</Label>
                                <Input
                                    id='consent-pair-name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='z.B. Aufklärung Hyaluron'
                                    autoFocus
                                />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label>Positiver Textbaustein (Einwilligung)</Label>
                                <Select value={positiveTextId} onValueChange={setPositiveTextId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder='Textbaustein auswählen...' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(allSnippets ?? []).map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <Label>Negativer Textbaustein (Ablehnung)</Label>
                                <Select value={negativeTextId} onValueChange={setNegativeTextId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder='Textbaustein auswählen...' />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(allSnippets ?? []).map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <label className='flex items-start gap-2 cursor-pointer'>
                                <Checkbox
                                    checked={required}
                                    onCheckedChange={(checked) => setRequired(checked === true)}
                                    className='mt-0.5'
                                />
                                <div className='flex flex-col'>
                                    <span className='text-sm font-medium'>Erforderlich</span>
                                    <span className='text-xs text-muted-foreground'>
                                        Wenn deaktiviert, steht in der Behandlung die Option „Nicht zutreffend“ zur Verfügung.
                                    </span>
                                </div>
                            </label>
                            <label className='flex items-start gap-2 cursor-pointer'>
                                <Checkbox
                                    checked={onlyRequiredForFemales}
                                    onCheckedChange={(checked) => setOnlyRequiredForFemales(checked === true)}
                                    className='mt-0.5'
                                />
                                <div className='flex flex-col'>
                                    <span className='text-sm font-medium'>Nur für weibliche Kunden erforderlich</span>
                                    <span className='text-xs text-muted-foreground'>
                                        Wenn aktiviert, wird diese Einwilligung nur bei weiblichen Kunden als Pflichtfeld behandelt.
                                    </span>
                                </div>
                            </label>
                        </div>
                        <SheetFooter>
                            {pair && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim() || !positiveTextId || !negativeTextId}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {pair ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Einwilligung löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie die Einwilligung &quot;{pair?.name}&quot; wirklich löschen?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

const ConsentPairsDataTable = () => {
    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedPair, setSelectedPair] = useState<ConsentPair | null>(null);

    const utils = trpc.useUtils();

    const { data: pairs, isLoading } = trpc.medicationConfig.getMandatoryTextPairs.useQuery();

    const createMutation = trpc.medicationConfig.createMandatoryTextPair.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getMandatoryTextPairs.invalidate();
            toast.success('Einwilligung erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen', { description: error.message });
        },
    });

    const updateMutation = trpc.medicationConfig.updateMandatoryTextPair.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getMandatoryTextPairs.invalidate();
            toast.success('Einwilligung erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren', { description: error.message });
        },
    });

    const deleteMutation = trpc.medicationConfig.deleteMandatoryTextPair.useMutation({
        onSuccess: () => {
            utils.medicationConfig.getMandatoryTextPairs.invalidate();
            toast.success('Einwilligung erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen', { description: error.message });
        },
    });

    const handleOpenCreate = () => {
        setSelectedPair(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (pair: ConsentPair) => {
        setSelectedPair(pair);
        setSheetOpen(true);
    };

    const handleSave = (data: { name: string; positiveTextId: string; negativeTextId: string; required: boolean; onlyRequiredForFemales: boolean }) => {
        if (selectedPair) {
            updateMutation.mutate({ id: selectedPair.id, ...data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleDelete = () => {
        if (selectedPair) {
            deleteMutation.mutate({ id: selectedPair.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading) {
        return <TableSkeleton columnCount={5} />;
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div />
                <Button onClick={handleOpenCreate}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neue Einwilligung
                </Button>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        <TableRow>
                            <TableHead className='font-medium text-gray-700'>Name</TableHead>
                            <TableHead className='font-medium text-gray-700'>Positiver Textbaustein</TableHead>
                            <TableHead className='font-medium text-gray-700'>Negativer Textbaustein</TableHead>
                            <TableHead className='font-medium text-gray-700'>Erforderlich</TableHead>
                            <TableHead className='font-medium text-gray-700 w-[80px]'>Aktionen</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(pairs ?? []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className='h-24 text-center text-muted-foreground'>
                                    Keine Einwilligungen vorhanden.
                                </TableCell>
                            </TableRow>
                        ) : (
                            (pairs ?? []).map((pair) => (
                                <TableRow key={pair.id}>
                                    <TableCell className='font-medium'>{pair.name}</TableCell>
                                    <TableCell>{pair.positiveText.name}</TableCell>
                                    <TableCell>{pair.negativeText.name}</TableCell>
                                    <TableCell>{pair.required ? 'Ja' : 'Nein'}</TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem onClick={() => handleOpenEdit(pair as ConsentPair)}>
                                                    <Pencil className='mr-2 h-4 w-4' />
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <ConsentPairSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                pair={selectedPair}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />
        </div>
    );
};

// ============================================
// Main Page Component
// ============================================

const MedicationConfig = () => {
    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-2xl font-bold mb-6'>Dokumentation</h1>
                <Tabs defaultValue='medicaments'>
                    <TabsList>
                        <TabsTrigger value='medicaments'>Medikamente</TabsTrigger>
                        <TabsTrigger value='units'>Einheiten</TabsTrigger>
                        <TabsTrigger value='regions'>Behandlungszonen</TabsTrigger>
                        <TabsTrigger value='snippets'>Doku-Snippets</TabsTrigger>
                        <TabsTrigger value='consent-pairs'>Einwilligung-Snippets</TabsTrigger>
                        <TabsTrigger value='icd'>ICD-Codes</TabsTrigger>
                    </TabsList>
                    <TabsContent value='medicaments'>
                        <MedicamentsDataTable />
                    </TabsContent>
                    <TabsContent value='units'>
                        <UnitsDataTable />
                    </TabsContent>
                    <TabsContent value='regions'>
                        <TreatmentRegionsDataTable />
                    </TabsContent>
                    <TabsContent value='snippets'>
                        <TreatmentTextSnippetsDataTable />
                    </TabsContent>
                    <TabsContent value='consent-pairs'>
                        <ConsentPairsDataTable />
                    </TabsContent>
                    <TabsContent value='icd'>
                        <IcdCodesDataTable />
                    </TabsContent>
                </Tabs>
            </div>
        </Content>
    );
};

export default MedicationConfig;
